import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';

import { pageTransitions } from '../../animations/page-transitions';
import { LocationService } from '../../services/location.service';
import { TranslationService } from '../../services/translation.service';
import { Step as StepperStep, StepClickedEvent, StepStatus, StepperComponent } from '../stepper/stepper.component';
import { DialogOkCancelComponent } from '../dialog-ok-cancel/dialog-ok-cancel.component';
import { AfterViewInitDirective } from '../../directives/after-view-init.directive';
import { LoadingSpinnerComponent } from '../utils/loading-spinner.component';
import { PageHeaderComponent } from '../page-header/page-header.component';
import { ScrollerDirective } from '../../directives/scroller.directive';
import { ECallOffService } from '../../../ecall-off/services/ecall-off.service';

interface WorkflowStep {
	labelTranslationKey: string;
	/** router path to access the page */
	path: string;
}

export interface WorkflowStepsData {
	afterSubmitUrl: string;
	backUrl?: string;
	steps: WorkflowStep[];
	titleTranslationKey?: string;
}

export interface WorkflowStepContent {
	/** data to pass between workflow steps */
	getUpdatedStepState?: () => Record<string, unknown> | undefined;
	isStepValidated?: () => boolean;
	/** set the current state in the step */
	setStepState?: (state: Record<string, unknown> | undefined) => void;
	submit?: () => Promise<void>;
	canGoBack?: () => boolean;
	isShipToMillCombinationValid?: () => boolean;
}

@Component({
	selector: 'copo-workflow-steps',
	templateUrl: './workflow-steps.component.html',
	styleUrl: './workflow-steps.component.css',
	animations: pageTransitions,
	imports: [
		ScrollerDirective,
		PageHeaderComponent,

		MatButtonModule,
		NgIf,
		LoadingSpinnerComponent,
		StepperComponent,
		RouterOutlet,
		AfterViewInitDirective,
		TranslateModule
	]
})
export class WorkflowStepsComponent implements OnInit {
	currentStepIndex = 0;
	currentStepState?: Record<string, unknown>;
	data?: WorkflowStepsData;
	isProcessing = false;
	nextError?: string;
	pageContent?: WorkflowStepContent;
	steps: StepperStep[] = [];

	constructor(
		private readonly changeRef: ChangeDetectorRef,
		private readonly locationService: LocationService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly ecallOffService: ECallOffService,
		private readonly translationService: TranslationService,

		private readonly dialog: MatDialog
	) {}

	ngOnInit(): void {
		firstValueFrom(this.route.data)
			.then(async data => {
				this.data = <WorkflowStepsData>data;
				this.initSteps();
				// in order to be sure to start at the first step when accessing the workflow by url
				// set te first step explicitly after setting the steps
				// done here because call after onActivate
				return this.setStep(0);
			})
			.catch((err: unknown) => {
				console.error('Error when getting activated route data', err);
			});
	}

	onActivate(pageContent: WorkflowStepContent) {
		this.pageContent = pageContent;
		if (this.pageContent.setStepState) {
			this.currentStepState = this.router.getCurrentNavigation()?.extras.state;
			this.pageContent.setStepState(this.currentStepState);
		}
		// canNext rely on pageContent and is used in template
		// need to notify angular about that change on activate because outside of standard lifecycle
		this.changeRef.detectChanges();
	}

	initSteps() {
		this.currentStepIndex = 0;
		if (!this.data || this.data.steps.length === 0) {
			this.steps = [];
			return;
		}
		this.steps = this.data.steps.map(step => ({
			label: this.translationService.getTR(step.labelTranslationKey),
			status: StepStatus.DISABLED
		}));
		this.steps[this.currentStepIndex].status = StepStatus.CURRENT;
	}

	goBack() {
		if (!this.data?.backUrl) {
			return;
		}
		this.locationService.go(-1, this.data.backUrl);
	}

	async goBackOrOpenDialog() {
		if (!this.pageContent?.canGoBack || this.pageContent.canGoBack()) {
			this.goBack();
			return;
		}
		if (this.steps.findIndex(step => step.status === StepStatus.CURRENT) === 0) {
			const dialogRef = this.dialog.open(DialogOkCancelComponent, {
				data: { title: 'quapp.action.go.back', content: 'dialog.loose-data-message' },
			});
			firstValueFrom(dialogRef.afterClosed())
				.then(result => {
					if (result) {
						this.goBack();
					}
				})
				.catch((error: unknown) => {
					console.error('Error during go back dialog', error);
				});
		} else {
			if (this.pageContent.getUpdatedStepState) {
				this.currentStepState = this.pageContent.getUpdatedStepState();
			}
			await this.setStep(this.currentStepIndex - 1, this.currentStepState);
		}
	}

	getNextButtonLabelTranslationKey() {
		if (this.currentStepIndex === (this.steps.length - 1)) {
			return 'ecall-off.submit-call-off';
		}
		return 'offers.action.next';
	}

	canNext() {
		if (!this.pageContent?.isStepValidated) {
			return true;
		}
		this.ecallOffService.updateUnloadingRef('');
		return this.pageContent.isStepValidated();
	}

	async setStep(index: number, state?: Record<string, unknown>) {
		// reset error
		this.nextError = undefined;
		// validate event index is valid
		if (index < 0
			|| index >= this.steps.length
			|| !this.data?.steps || index >= this.data.steps.length) {
			return;
		}
		// update step status
		this.currentStepIndex = index;
		for (let i = 0; i < this.steps.length; i++) {
			if (i < this.currentStepIndex) {
				this.steps[i].status = StepStatus.ENABLED;
			} else if (i === this.currentStepIndex) {
				this.steps[i].status = StepStatus.CURRENT;
			} else {
				this.steps[i].status = StepStatus.DISABLED;
			}
		}
		// navigate to the step url
		const relativePath = this.data.steps[this.currentStepIndex].path;
		await this.router.navigate([relativePath], { relativeTo: this.route, state });
	}

	async onStepClicked(event: StepClickedEvent) {
		if (this.isProcessing) {
			return;
		}
		try {
			this.isProcessing = true;
			if (this.pageContent?.getUpdatedStepState) {
				this.currentStepState = this.pageContent.getUpdatedStepState();
			}
			await this.setStep(event.stepIndex, this.currentStepState);
		} catch (error) {
			console.error(`Error setting step ${ event.stepIndex }`, error);
		} finally {
			this.isProcessing = false;
		}
	}

	async onNext() {
		if (this.isProcessing) {
			return;
		}
		if (this.currentStepIndex === 1) {
			this.ecallOffService.triggerNext();
		}
		if (this.currentStepIndex === 1 && this.pageContent?.isShipToMillCombinationValid
			&& !this.pageContent.isShipToMillCombinationValid()) {
			return;
		}
		try {
			this.isProcessing = true;
			this.nextError = undefined;
			if (this.pageContent?.getUpdatedStepState) {
				this.currentStepState = this.pageContent.getUpdatedStepState();
			}
			if (this.currentStepIndex === (this.steps.length - 1)) {
				if (this.pageContent?.submit) {
					await this.pageContent.submit();
				}
				await this.router.navigate([this.data?.afterSubmitUrl ?? '/']);
				return;
			}
			await this.setStep(this.currentStepIndex + 1, this.currentStepState);
		} catch (error) {
			console.error('Error going next step', error);
			this.nextError = 'workflow-steps.next.error';
		} finally {
			this.isProcessing = false;
		}
	}
}
